'use client';

/**
 * 오가닉 영상 카드의 지금 유효한 재생 주소 — 화면 가까이 온 카드만 모아 한 번에 묻는다.
 *
 * 카드가 `need(item)` 을 부르면 잠깐(`FLUSH_MS`) 모았다가 최대 24건씩 묻는다. `pending` 은 다시 묻되 서버가 받는 중인 건을
 * `RETRY_WAIT_MS` 만큼 기다려 주게 하고(롱폴), 다시 묻는 주기는 `VIDEO_URL_RETRY_MS` 를 지킨다.
 * 다시 묻는 것은 **아직 원하는 카드뿐이다** — 화면을 떠난 카드는 `release(id)` 로 빠지고, 돌아와 `need` 를 부르면 이어 묻는다.
 * 요청이 통째로 실패하면(시간 초과 등) 그 건들도 같은 주기로 다시 묻는다.
 * 다시 묻기를 다 쓰고도 받는 중이면 `unsupported`(`VIDEO_URL_GAVE_UP_REASON`)로 접는다 — 카드가 「미리보기 없음」으로 멈춘다.
 * 재생에 실패하면 `report` 로 신고한 뒤 한 번 더 묻는다. 결과는 게시물 id 로 찾는다.
 *
 * ⚠️ 주소 캐시는 수집 백엔드가 쥔다 — 여기 결과는 이 화면이 떠 있는 동안만 산다.
 */

import type { VideoUrlResult, VideoUrlRequestItem } from 'src/types/search-all/video-url';

import { useRef, useState, useEffect, useCallback } from 'react';

import { fetchVideoUrls, reportVideoUrlFailure } from 'src/actions/search-all-video-url';

import { VIDEO_URL_BATCH_MAX } from 'src/types/search-all/video-url';

import {
  shouldAskVideoUrl,
  exhaustedVideoUrl,
  VIDEO_URL_RETRY_MS,
  videoUrlRetryDelay,
  isVideoUrlExhausted,
} from '../../../utils/search-all-video-url';

/** 카드가 연달아 들어올 때 모으는 시간(ms) */
const FLUSH_MS = 150;
/**
 * 다시 물을 때 서버가 받는 중인 건을 붙잡고 기다려 줄 시간(ms) — 상한 `VIDEO_URL_WAIT_MAX_MS` 안.
 * ⚠️ **처음 묻는 건에는 걸지 않는다** — 한 배치의 답이 함께 오므로 캐시에 있던 주소까지 이만큼 늦게 뜬다.
 */
const RETRY_WAIT_MS = 2500;

export function useSearchAllVideoUrls() {
  const [results, setResults] = useState<Map<string, VideoUrlResult>>(() => new Map());

  const resultsRef = useRef(results);
  resultsRef.current = results;
  const queue = useRef(new Map<string, VideoUrlRequestItem>());
  const inflight = useRef(new Set<string>());
  const attempts = useRef(new Map<string, number>());
  const known = useRef(new Map<string, VideoUrlRequestItem>());
  /** 지금 원하는 id — `need` 로 들어오고 `release` 로 빠진다. 다시 묻기는 여기 있는 것만 */
  const wanted = useRef(new Set<string>());
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const alive = useRef(true);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    alive.current = true;
    abort.current = new AbortController();
    const pending = timers.current;
    const ctrl = abort.current;
    return () => {
      alive.current = false;
      ctrl.abort();
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      if (alive.current) fn();
    }, ms);
    timers.current.add(t);
  }, []);

  // 자기 자신을 다시 예약하므로 ref 로 부른다 — 늘 최신 함수를 가리킨다
  const flushRef = useRef<() => Promise<void>>(async () => undefined);

  flushRef.current = async () => {
    const queued = [...queue.current.values()];
    // 처음 묻는 건을 먼저, 기다리지 않고 보낸다 — 다시 묻는 건은 다음 차례에 기다려 주는 요청으로 간다
    const fresh = queued.filter((item) => (attempts.current.get(item.id) ?? 0) === 0);
    const waitMs = fresh.length > 0 ? 0 : RETRY_WAIT_MS;
    const batch = (fresh.length > 0 ? fresh : queued).slice(0, VIDEO_URL_BATCH_MAX);
    if (batch.length === 0) return;
    batch.forEach((item) => {
      queue.current.delete(item.id);
      inflight.current.add(item.id);
      attempts.current.set(item.id, (attempts.current.get(item.id) ?? 0) + 1);
    });
    if (queue.current.size > 0) later(() => flushRef.current(), FLUSH_MS);

    const startedAt = Date.now();
    const got = await fetchVideoUrls(batch, waitMs, abort.current?.signal).catch(() => null);
    if (!alive.current) return;
    batch.forEach((item) => inflight.current.delete(item.id));
    // 통째로 실패했으면 묻던 건 전부, 아니면 아직 받는 중인 건만 다시 묻는다 — 다시 묻기를 다 쓴 건은 접는다
    const unsettled: [string, VideoUrlResult | undefined][] = got
      ? got.filter((r) => r.status === 'pending' || r.status === 'stale').map((r) => [r.id, r])
      : batch.map((item) => [item.id, resultsRef.current.get(item.id)]);
    const exhausted = (id: string, last: VideoUrlResult | undefined) =>
      isVideoUrlExhausted(last, attempts.current.get(id) ?? 0);
    const settled = [
      ...(got ?? []),
      ...unsettled.filter(([id, last]) => exhausted(id, last)).map(([id, last]) => exhaustedVideoUrl(id, last)),
    ];
    if (settled.length > 0) {
      setResults((prev) => {
        const next = new Map(prev);
        settled.forEach((r) => next.set(r.id, r));
        return next;
      });
    }
    const retry = unsettled.filter(([id, last]) => !exhausted(id, last));
    if (retry.length > 0) {
      later(() => {
        retry.forEach(([id, last]) => {
          const item = known.current.get(id);
          if (!item || !wanted.current.has(id) || inflight.current.has(id)) return;
          if (shouldAskVideoUrl(last, attempts.current.get(id) ?? 0)) queue.current.set(id, item);
        });
        flushRef.current();
      }, videoUrlRetryDelay(startedAt, Date.now()));
    }
  };

  /** 카드가 화면 가까이 왔다 — 필요하면 묻는다 */
  const need = useCallback((item: VideoUrlRequestItem) => {
    known.current.set(item.id, item);
    wanted.current.add(item.id);
    if (queue.current.has(item.id) || inflight.current.has(item.id)) return;
    const last = resultsRef.current.get(item.id);
    const asked = attempts.current.get(item.id) ?? 0;
    // 신고 뒤 무효화된 건이 다시 묻기를 다 썼다 — 접어서 받는 중 표시를 걷는다
    if (isVideoUrlExhausted(last, asked)) {
      setResults((prev) => new Map(prev).set(item.id, exhaustedVideoUrl(item.id, last)));
      return;
    }
    if (!shouldAskVideoUrl(last, asked)) return;
    const first = queue.current.size === 0;
    queue.current.set(item.id, item);
    if (first) later(() => flushRef.current(), FLUSH_MS);
  }, [later]);

  /** 카드가 화면을 떠났다 — 줄에서 빼고 다시 묻기를 멈춘다(이미 나간 요청은 그대로 받는다) */
  const release = useCallback((id: string) => {
    wanted.current.delete(id);
    queue.current.delete(id);
  }, []);

  /** 재생 실패 — 신고하고 무효화로 표시한 뒤 다시 묻는다 */
  const report = useCallback((id: string, reason: string) => {
    reportVideoUrlFailure(id, reason);
    const failed = resultsRef.current.get(id);
    const stale: VideoUrlResult = { ...(failed ?? { id, videoUrl: '', posterUrl: '', expiresAt: null, reason: '' }), status: 'stale', videoUrl: '' };
    setResults((prev) => new Map(prev).set(id, stale));
    const item = known.current.get(id);
    if (item) later(() => need(item), VIDEO_URL_RETRY_MS);
  }, [later, need]);

  return { results, need, release, report };
}
